import { ConstraintComparison, ConstraintTarget, ConstraintTypes } from "../../constants/constraints";
import dateSubtractionFunctions from "../../modules/definition/calendar/dateFunctions/dateSubtractionFunctions";
import { addTimespans } from "../../modules/definition/calendar/dateFunctions/timespanFunctions";
import { Precision } from "../../types";
import { ConstraintRecord } from "../../types/immutableRecords/model/data/ConstraintRecord";
import uniqueId from "../../utils/uniqueId";
import { updateDate, updateDateWithOffset, updateEndDate } from "./updateDates";

/**
 * This is not a pure function: maxDisplayId is mutated
 * @param {object} v2Entity
 * @param {object} definitions
 * @param {string} newTypeIdForV2Id
 * @param {object} maxDisplayId
 * @param {object} v2Data
 * @param {boolean} updateDatesInBC
 * @param {CalendarRecord} calendarRecord
 */
const upgradeEntity = (
  v2Entity,
  definitions,
  newTypeIdForV2Id,
  maxDisplayId,
  v2Data,
  updateDatesInBC,
  calendarRecord,
  dateConstraints
) => {
  const typeId = newTypeIdForV2Id[v2Entity.entityType];
  const type = definitions.types.byId[typeId];
  const prefix = type.identifierPrefix;
  const index = maxDisplayId[typeId] ? maxDisplayId[typeId] + 1 : 1;
  maxDisplayId[typeId] = index;

  let dateConstraintRecords = [];
  let startRecord = null;
  let endRecord = null;
  let durationRecord = null;
  const dateConstraintIds = [];
  let ongoing = false;

  // Get entity start date
  if (v2Entity.createRangePosition) {
    const start = {
      timestamp: v2Entity.createRangePosition.timestamp,
      precision: Precision.fromString(v2Entity.createRangePosition.precision)
    };
    startRecord = updateDate(calendarRecord, updateDatesInBC, start);
  } else if (v2Entity.createEvent) {
    const createEvent = v2Data.events.find(i => i.guid === v2Entity.createEvent.eventGuid);
    const start = {
      timestamp: createEvent.rangeValues[0].position.timestamp,
      precision: Precision.fromString(createEvent.rangeValues[0].position.precision)
    };
    const offset = v2Entity.createEvent.addToEnd
      ? addTimespans(v2Entity.createEvent.offset, createEvent.rangeValues[0].span)
      : v2Entity.createEvent.offset;

    startRecord = updateDateWithOffset(calendarRecord, updateDatesInBC, start, offset);

    // Add date constraint
    const dateConstraintId = uniqueId();
    const startConstraintRecord = ConstraintRecord({
      id: dateConstraintId,
      constraintType: ConstraintTypes.Relative,
      subject: v2Entity.guid,
      subjectTarget: ConstraintTarget.Start,
      object: v2Entity.createEvent.eventGuid,
      objectTarget: v2Entity.createEvent.addToEnd ? ConstraintTarget.End : ConstraintTarget.Start,
      comparison: ConstraintComparison.Equal,
      offset: v2Entity.createEvent.offset
    });

    const constraintObj = startConstraintRecord.toJS();
    dateConstraintRecords.push(constraintObj);
    // Add to the global list
    dateConstraints.push(constraintObj);
    dateConstraintIds.push(dateConstraintId);
  }

  // Get entity end date
  if (v2Entity.destroyRangePosition) {
    const end = {
      timestamp: v2Entity.destroyRangePosition.timestamp,
      precision: Precision.fromString(v2Entity.destroyRangePosition.precision)
    };
    endRecord = updateEndDate(calendarRecord, updateDatesInBC, end);
  } else if (v2Entity.destroyEvent) {
    const destroyEvent = v2Data.events.find(i => i.guid === v2Entity.destroyEvent.eventGuid);
    const end = {
      timestamp: destroyEvent.rangeValues[0].position.timestamp,
      precision: Precision.fromString(destroyEvent.rangeValues[0].position.precision)
    };
    const offset = v2Entity.destroyEvent.addToEnd
      ? addTimespans(v2Entity.destroyEvent.offset, destroyEvent.rangeValues[0].span)
      : v2Entity.destroyEvent.offset;

    endRecord = updateDateWithOffset(calendarRecord, updateDatesInBC, end, offset);

    // Add date constraint
    const dateConstraintId = uniqueId();
    const startConstraintRecord = ConstraintRecord({
      id: dateConstraintId,
      constraintType: ConstraintTypes.Relative,
      subject: v2Entity.guid,
      subjectTarget: ConstraintTarget.End,
      object: v2Entity.destroyEvent.eventGuid,
      objectTarget: v2Entity.destroyEvent.addToEnd ? ConstraintTarget.End : ConstraintTarget.Start,
      comparison: ConstraintComparison.Equal,
      offset: v2Entity.destroyEvent.offset
    });
    const constraintObj = startConstraintRecord.toJS();
    dateConstraintRecords.push(constraintObj);
    // Add to the global list
    dateConstraints.push(constraintObj);
    dateConstraintIds.push(dateConstraintId);
  }

  // If there's an end date and no start date, set the start date
  if (!startRecord && endRecord) {
    startRecord = endRecord;
  }

  // If there's a start date and no end date, set as ongoing
  if (startRecord && !endRecord) {
    ongoing = true;
  }

  // Get entity duration
  if (startRecord && endRecord) {
    durationRecord = dateSubtractionFunctions.timespanBetweenDates(calendarRecord, startRecord, endRecord);
  }

  return {
    id: v2Entity.guid,
    type: typeId,
    label: v2Entity.name,
    displayId: prefix + index,
    initials: v2Entity.name
      .split(" ")
      .slice(0, 2)
      .map(w => w.charAt(0))
      .join(""),
    color: v2Entity.swatchColor,
    summary: v2Entity.notes,
    references: [],
    inverseReferences: [],
    startDate: startRecord ? startRecord.toObject() : {},
    preferredStartDate: startRecord ? startRecord.toObject() : {},
    duration: durationRecord ? durationRecord.toObject() : {},
    preferredDuration: durationRecord ? durationRecord.toObject() : {},
    dateConstraints: dateConstraintIds,
    ongoing: ongoing,
    sortOrder: v2Entity.sortOrder,
    dateConstraintRecords: dateConstraintRecords
  };
};

export default upgradeEntity;
